import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useMatchingBewerber } from '../../../api/bedarfe';
import { useCreateKennenlernen } from '../../../api/kennenlernen';
import type { MatchingBewerber, MatchType } from '../../../types/kennenlernen';

interface SelectBewerberKennenlernenModalProps {
  isOpen: boolean;
  onClose: () => void;
  bedarfId: string;
  childName: string;
  parentEmail?: string | null;
  onSuccess?: () => void;
}

interface FormData {
  bewerberId: string;
  scheduledAt: string;
  location: string;
  notes: string;
}

const MATCH_TYPE_LABELS: Record<MatchType, { label: string; color: string }> = {
  BOTH: { label: 'Wohnort & Ort Stelle', color: 'bg-green-100 text-green-800' },
  WOHNORT: { label: 'Wohnort', color: 'bg-blue-100 text-blue-800' },
  ORTSTELLE: { label: 'Ort Stelle', color: 'bg-purple-100 text-purple-800' },
  // Reachable in Stadt mode too: someone who applied to this Bedarf is listed
  // even when neither their home city nor their applied-for cities match.
  BEWERBUNG: { label: 'Bewerbung', color: 'bg-primary-100 text-primary-800' },
  // This modal always queries in Stadt mode, so UMKREIS cannot occur here — it
  // is listed only to keep the label map exhaustive.
  UMKREIS: { label: 'Umkreis', color: 'bg-gray-100 text-gray-800' },
};

export function SelectBewerberKennenlernenModal({
  isOpen,
  onClose,
  bedarfId,
  childName,
  parentEmail,
  onSuccess,
}: SelectBewerberKennenlernenModalProps) {
  const createMutation = useCreateKennenlernen();
  const { data: matchingData, isLoading: isLoadingBewerber } = useMatchingBewerber(bedarfId, isOpen);
  const [error, setError] = useState<string | null>(null);
  const [selectedBewerber, setSelectedBewerber] = useState<MatchingBewerber | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      bewerberId: '',
      scheduledAt: '',
      location: '',
      notes: '',
    },
  });

  const watchBewerberId = watch('bewerberId');

  // Update selected Bewerber when dropdown changes
  const handleBewerberChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const bewerberId = e.target.value;
    if (bewerberId && matchingData?.data) {
      const bewerber = matchingData.data.find(b => b.id === bewerberId);
      setSelectedBewerber(bewerber || null);
    } else {
      setSelectedBewerber(null);
    }
  };

  const handleFormSubmit = async (data: FormData) => {
    setError(null);
    try {
      await createMutation.mutateAsync({
        bedarfId,
        bewerberId: data.bewerberId,
        scheduledAt: new Date(data.scheduledAt).toISOString(),
        location: data.location || undefined,
        notes: data.notes || undefined,
        sendEmails: true, // Always send emails when creating from Bedarf context
      });
      toast.success('Kennenlernen erfolgreich geplant und Einladungen versendet');
      reset();
      setSelectedBewerber(null);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError('Fehler beim Erstellen des Kennenlernens');
      toast.error('Fehler beim Erstellen des Kennenlernens');
      console.error('Failed to create kennenlernen:', err);
    }
  };

  const handleClose = () => {
    reset();
    setSelectedBewerber(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const matchingBewerber = matchingData?.data || [];

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Kennenlernen planen
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Planen Sie ein Kennenlernen fuer <span className="font-medium">{childName}</span>.
                  </p>

                  {error && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  <div className="mt-4 space-y-4">
                    {/* Bewerber Selection */}
                    <div>
                      <label htmlFor="bewerberId" className="block text-sm font-medium text-gray-700">
                        Bewerber auswaehlen *
                      </label>
                      <select
                        id="bewerberId"
                        {...register('bewerberId', { required: 'Bitte waehlen Sie einen Bewerber' })}
                        onChange={(e) => {
                          register('bewerberId').onChange(e);
                          handleBewerberChange(e);
                        }}
                        disabled={isLoadingBewerber}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
                      >
                        <option value="">
                          {isLoadingBewerber ? 'Lade passende Bewerber...' : '-- Bewerber auswaehlen --'}
                        </option>
                        {matchingBewerber.map((bewerber) => (
                          <option key={bewerber.id} value={bewerber.id}>
                            {bewerber.firstName} {bewerber.lastName} - {bewerber.city || bewerber.ortStellen.join(', ') || 'Ohne Ort'}
                          </option>
                        ))}
                      </select>
                      {matchingBewerber.length === 0 && !isLoadingBewerber && (
                        <p className="mt-1 text-sm text-yellow-600">
                          Keine passenden Bewerber gefunden. Ueberpruefen Sie die Schulstadt des Bedarfs.
                        </p>
                      )}
                      {errors.bewerberId && (
                        <p className="mt-1 text-sm text-red-600">{errors.bewerberId.message}</p>
                      )}
                    </div>

                    {/* Selected Bewerber Info */}
                    {selectedBewerber && (
                      <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-700">
                            {selectedBewerber.firstName} {selectedBewerber.lastName}
                          </p>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${MATCH_TYPE_LABELS[selectedBewerber.matchType].color}`}>
                            {MATCH_TYPE_LABELS[selectedBewerber.matchType].label}
                          </span>
                        </div>
                        {selectedBewerber.city && (
                          <p className="text-sm text-gray-500">
                            Wohnort: {selectedBewerber.city}
                          </p>
                        )}
                        {selectedBewerber.ortStellen.length > 0 && (
                          <p className="text-sm text-gray-500">
                            Ort Stelle: {selectedBewerber.ortStellen.join(', ')}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Date/Time */}
                    <div>
                      <label htmlFor="scheduledAt" className="block text-sm font-medium text-gray-700">
                        Termin *
                      </label>
                      <input
                        type="datetime-local"
                        id="scheduledAt"
                        {...register('scheduledAt', { required: 'Termin ist erforderlich' })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                      {errors.scheduledAt && (
                        <p className="mt-1 text-sm text-red-600">{errors.scheduledAt.message}</p>
                      )}
                    </div>

                    {/* Location */}
                    <div>
                      <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                        Ort
                      </label>
                      <input
                        type="text"
                        id="location"
                        {...register('location')}
                        placeholder="z.B. Schule, Buero..."
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>

                    {/* Notes */}
                    <div>
                      <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                        Notizen
                      </label>
                      <textarea
                        id="notes"
                        rows={2}
                        {...register('notes')}
                        placeholder="Zusaetzliche Informationen..."
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>

                    <p className="text-xs text-gray-500">
                      Eine Einladung wird automatisch an den Bewerber
                      {parentEmail ? ' und die Eltern' : ''} gesendet.
                      {!parentEmail && (
                        <span className="text-yellow-600"> (Keine Eltern-E-Mail hinterlegt)</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={createMutation.isPending || isLoadingBewerber || !watchBewerberId}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {createMutation.isPending ? 'Wird erstellt...' : 'Kennenlernen planen'}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}
